import { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useAppSelector } from '@/hooks/redux';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { authManager } from '@/services/authManager';
import { ScheduleNotification } from '@/services/notificationService';

const UNREAD_COUNT_CACHE_KEY = 'notification_unread_count';
const RECENT_NOTIFICATIONS_CACHE_KEY = 'notification_recent';

// Cache helpers
const getCachedUnreadCount = (): number => {
  try {
    const cached = localStorage.getItem(UNREAD_COUNT_CACHE_KEY);
    return cached ? parseInt(cached, 10) || 0 : 0;
  } catch {
    return 0;
  }
};

const setCachedUnreadCount = (count: number): void => {
  try {
    localStorage.setItem(UNREAD_COUNT_CACHE_KEY, count.toString());
  } catch { /* ignore localStorage errors */ }
};

const getCachedRecentNotifications = (): { notifications: ScheduleNotification[]; total: number } | null => {
  try {
    const cached = localStorage.getItem(RECENT_NOTIFICATIONS_CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCachedRecentNotifications = (notifications: ScheduleNotification[], total: number): void => {
  try {
    localStorage.setItem(RECENT_NOTIFICATIONS_CACHE_KEY, JSON.stringify({ notifications, total }));
  } catch { /* ignore localStorage errors */ }
};

export interface SyncProgressEvent {
  jobId?: string;
  leagueId?: string;
  organizationId?: string;
  status?: 'in_progress' | 'completed' | 'error';
  message?: string;
  triggeredBy?: string;
  leagueName?: string;
  division?: string;
  result?: {
    matchesFound: number;
    matchesCreated: number;
    matchesUpdated: number;
    matchesDeleted: number;
    duration: number;
  };
  error?: string;
}

interface UseEventStreamOptions {
  // Notification callbacks
  onNotification?: (notification: ScheduleNotification) => void;
  // Sync callbacks
  onSyncStart?: (leagueId: string, jobId: string, event: SyncProgressEvent) => void;
  onSyncProgress?: (leagueId: string, message: string) => void;
  onSyncComplete?: (leagueId: string, result: SyncProgressEvent['result'], event: SyncProgressEvent) => void;
  onSyncError?: (leagueId: string, error: string, event: SyncProgressEvent) => void;
}

/**
 * Unified event stream hook - multiplexes notifications and sync progress over one SSE connection
 */
export const useEventStream = (options: UseEventStreamOptions = {}) => {
  const { user } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();

  // Notification state
  const [unreadCount, setUnreadCount] = useState(getCachedUnreadCount);
  const [latestNotification, setLatestNotification] = useState<ScheduleNotification | null>(null);

  // Sync state
  const [syncingLeagues, setSyncingLeagues] = useState<Set<string>>(new Set());

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  // Guard: suppress SSE unreadCount overwrite briefly after a local decrement
  // to prevent race where SSE reconnects before server processes markAsRead
  const countGuardUntilRef = useRef(0);

  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  // Shared message handler extracted so both initial connect and visibility reconnect use it
  const handleMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'connected':
        console.log('[EventStream] Connection confirmed', {
          org: data.organizationId,
          parent: data.parentId,
          unreadCount: data.unreadCount,
          recentNotificationCount: data.recentNotifications?.length,
        });
        if (typeof data.unreadCount === 'number') {
          // Skip if we recently decremented locally (prevents race where SSE
          // reconnects before server has processed markAsRead)
          if (Date.now() < countGuardUntilRef.current) {
            console.log('[EventStream] Skipping SSE unreadCount overwrite (guard active)');
          } else {
            setUnreadCount(data.unreadCount);
            setCachedUnreadCount(data.unreadCount);
          }
        }
        if (data.recentNotifications) {
          const cacheData = {
            notifications: data.recentNotifications,
            total: data.totalNotifications || data.recentNotifications.length,
            page: 1,
            pageSize: 5,
            hasMore: (data.totalNotifications || 0) > 5,
          };
          queryClient.setQueryData(['notifications', 'recent'], cacheData);
          setCachedRecentNotifications(data.recentNotifications, cacheData.total);
        }
        break;

      case 'heartbeat':
        break;

      case 'notification':
        console.log('[EventStream] notification received:', data.notification);
        if (data.notification) {
          setLatestNotification(data.notification);
          setUnreadCount((prev) => {
            const newCount = prev + 1;
            console.log('[EventStream] unreadCount incremented:', prev, '->', newCount);
            setCachedUnreadCount(newCount);
            return newCount;
          });
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
          callbacksRef.current.onNotification?.(data.notification);
        }
        break;

      case 'sync_progress':
        console.log('[EventStream] sync_progress received:', {
          jobId: data.jobId,
          leagueId: data.leagueId,
          status: data.status,
          timestamp: Date.now()
        });

        if (!data.leagueId) break;

        if (data.status === 'in_progress') {
          setSyncingLeagues(prev => new Set(prev).add(data.leagueId));

          if (data.message === 'Starting import process...' && data.jobId) {
            callbacksRef.current.onSyncStart?.(data.leagueId, data.jobId, data);
          } else {
            callbacksRef.current.onSyncProgress?.(data.leagueId, data.message || '');
          }
        } else if (data.status === 'completed') {
          console.log('[EventStream] Processing COMPLETED event:', {
            jobId: data.jobId,
            leagueId: data.leagueId
          });

          setSyncingLeagues(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.leagueId);
            return newSet;
          });

          queryClient.invalidateQueries({
            predicate: (query) => {
              const key = query.queryKey;
              if (!Array.isArray(key)) return false;
              return key[0] === 'leagues'
                || (key[0] === 'league' && key[1] === data.leagueId)
                || key[0] === 'conflict-summary'
                || (key[0] === 'contextual-standings' && key[1] === data.leagueId);
            }
          });

          callbacksRef.current.onSyncComplete?.(data.leagueId, data.result, data);
        } else if (data.status === 'error') {
          setSyncingLeagues(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.leagueId);
            return newSet;
          });

          queryClient.invalidateQueries({ queryKey: ['leagues'] });
          callbacksRef.current.onSyncError?.(data.leagueId, data.error || 'Update failed', data);
        }
        break;
    }
  }, [queryClient]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Pre-populate cache on mount
  useEffect(() => {
    const cached = getCachedRecentNotifications();
    if (cached) {
      queryClient.setQueryData(['notifications', 'recent'], {
        notifications: cached.notifications,
        total: cached.total,
        page: 1,
        pageSize: 5,
        hasMore: cached.total > 5,
      });
    }
  }, [queryClient]);

  // Connect when user/org is available
  // Use abort flag to handle React Strict Mode double-invocation
  useEffect(() => {
    let aborted = false;

    const connectIfNotAborted = async () => {
      if (!user?.currentOrganizationId) {
        return;
      }

      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Ensure we have a fresh token
      await authManager.getAccessToken();

      // Check if we were aborted during the async operation
      if (aborted) {
        console.log('[EventStream] Connection aborted during token fetch');
        return;
      }

      const token = authManager.getCurrentToken();
      const streamUrl = token
        ? `/api/v1/events/stream?token=${encodeURIComponent(token)}`
        : `/api/v1/events/stream`;

      const es = new EventSource(streamUrl);
      eventSourceRef.current = es;

      es.onopen = () => {
        if (aborted) {
          es.close();
          return;
        }
        console.log('[EventStream] Connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      es.onmessage = (event) => {
        if (aborted) return;
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (error) {
          console.error('[EventStream] Failed to parse event:', error);
        }
      };

      es.onerror = () => {
        if (aborted) return;
        console.error('[EventStream] Connection error');
        setIsConnected(false);

        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`[EventStream] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            if (!aborted) {
              reconnectAttemptsRef.current++;
              connectIfNotAborted();
            }
          }, delay);
        } else {
          console.error('[EventStream] Max reconnection attempts reached');
        }
      };
    };

    connectIfNotAborted();

    return () => {
      aborted = true;
      disconnect();
    };
  }, [user?.currentOrganizationId, queryClient, disconnect, handleMessage]);

  // Disconnect SSE when tab is hidden, reconnect when visible
  useEffect(() => {
    if (!user?.currentOrganizationId) return;

    if (!isVisible) {
      // Tab hidden — close the SSE connection to save battery
      disconnect();
    } else if (!eventSourceRef.current) {
      // Tab visible again and no connection — reconnect
      const reconnect = async () => {
        await authManager.getAccessToken();
        const token = authManager.getCurrentToken();
        const streamUrl = token
          ? `/api/v1/events/stream?token=${encodeURIComponent(token)}`
          : `/api/v1/events/stream`;

        const es = new EventSource(streamUrl);
        eventSourceRef.current = es;

        es.onopen = () => {
          console.log('[EventStream] Reconnected after visibility change');
          setIsConnected(true);
          reconnectAttemptsRef.current = 0;
        };

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            handleMessage(data);
          } catch (error) {
            console.error('[EventStream] Failed to parse event:', error);
          }
        };

        es.onerror = () => {
          console.error('[EventStream] Reconnection error after visibility change');
          setIsConnected(false);
        };
      };
      reconnect();
    }
  }, [isVisible, user?.currentOrganizationId, disconnect, handleMessage]);

  // Notification helpers
  const decrementUnreadCount = useCallback((count = 1) => {
    // Suppress SSE count overwrite for 5s to avoid race with markAsRead
    countGuardUntilRef.current = Date.now() + 5000;
    setUnreadCount((prev) => {
      const newCount = Math.max(0, prev - count);
      setCachedUnreadCount(newCount);
      return newCount;
    });
  }, []);

  const resetUnreadCount = useCallback(() => {
    countGuardUntilRef.current = Date.now() + 5000;
    setUnreadCount(0);
    setCachedUnreadCount(0);
  }, []);

  return {
    // Connection
    isConnected,
    disconnect,
    // Notifications
    unreadCount,
    latestNotification,
    decrementUnreadCount,
    resetUnreadCount,
    // Sync
    syncingLeagues,
  };
};
